import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileCheck2, Plus, X, Play, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import api from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { ROLES } from "../../../lib/roles";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";

const RESERVA_TIPOS = ["intereses", "operativos", "otras"];

function money(n, cur = "MXN") {
    return Number(n || 0).toLocaleString("es-MX", {
        style: "currency", currency: cur, maximumFractionDigits: 0,
    });
}

export default function BorrowingBaseTab({ facility }) {
    const { hasRole } = useAuth();
    const [actual, setActual] = useState(null);
    const [historico, setHistorico] = useState([]);
    const [cortes, setCortes] = useState([]);
    const [corteId, setCorteId] = useState("");
    const [fx, setFx] = useState("1");
    const [reservas, setReservas] = useState([{ tipo: "intereses", monto: 0, moneda: facility.moneda }]);
    const [busy, setBusy] = useState(false);

    const canCalc = hasRole([
        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.RIESGOS, ROLES.FINANZAS, ROLES.ADMIN_TI,
    ]);

    const load = async () => {
        const [{ data: a }, { data: h }, { data: c }] = await Promise.all([
            api.get(`/facilities/${facility.id}/borrowing-base/actual`),
            api.get(`/facilities/${facility.id}/borrowing-base/historico`),
            api.get("/tape/cortes"),
        ]);
        setActual(a?.error ? null : a);
        setHistorico(h);
        const cs = (c || []).filter((x) => x.facility_id === facility.id);
        setCortes(cs);
        if (!corteId && cs.length) setCorteId(cs[0].corte_id);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [facility.id]);

    const addReserva = () => setReservas((r) => [...r, { tipo: "operativos", monto: 0, moneda: facility.moneda }]);
    const rmReserva = (i) => setReservas((r) => r.filter((_, idx) => idx !== i));
    const setReserva = (i, k, v) => setReservas((r) => r.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

    const calcular = async () => {
        if (!corteId) return toast.error("Selecciona corte");
        setBusy(true);
        try {
            const payload = {
                corte_id: corteId,
                tipo_cambio: parseFloat(fx || 1),
                reservas: reservas.map((r) => ({ ...r, monto: parseFloat(r.monto || 0) })),
            };
            const { data } = await api.post(`/facilities/${facility.id}/borrowing-base/calcular`, payload);
            toast.success(`BB calculado: ${money(data.borrowing_base_neto, facility.moneda)}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const cushionUp = useMemo(() => {
        if (!actual || historico.length < 2) return null;
        const prev = historico[1];
        return Number(actual.cushion || 0) - Number(prev.cushion || 0);
    }, [actual, historico]);

    return (
        <div className="space-y-6" data-testid="tab-bb-content">
            <div className="flex items-center gap-2">
                <FileCheck2 className="h-5 w-5 text-[color:var(--brand-dark-gold)]" />
                <h3 className="font-display text-2xl">Borrowing Base</h3>
            </div>

            {actual ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="bb-kpis">
                    <div className="kpi-card">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">BB neto</div>
                        <div className="kpi-value mt-2 text-2xl">{money(actual.borrowing_base_neto, facility.moneda)}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">Corte {actual.fecha_corte}</div>
                    </div>
                    <div className="kpi-card">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cushion</div>
                        <div className={`kpi-value mt-2 text-2xl ${Number(actual.cushion) < 0 ? "text-[color:var(--brand-coral)]" : "text-[color:var(--brand-exito)]"}`}>
                            {money(actual.cushion, facility.moneda)}
                        </div>
                        {cushionUp !== null && (
                            <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                                {cushionUp >= 0 ? (
                                    <TrendingUp className="h-3 w-3 text-[color:var(--brand-exito)]" />
                                ) : (
                                    <TrendingDown className="h-3 w-3 text-[color:var(--brand-coral)]" />
                                )}
                                {money(Math.abs(cushionUp), facility.moneda)} vs prev
                            </div>
                        )}
                    </div>
                    <div className="kpi-card">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Utilización</div>
                        <div className="kpi-value mt-2 text-2xl">{Number(actual.utilizacion_pct || 0).toFixed(1)}%</div>
                    </div>
                    <div className="kpi-card">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Bruto elegible</div>
                        <div className="kpi-value mt-2 text-2xl">{money(actual.monto_bruto_elegible, facility.moneda)}</div>
                    </div>
                </div>
            ) : (
                <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                    Aún no hay Borrowing Base calculado. Ejecuta primero elegibilidad y luego calcula BB.
                </div>
            )}

            {canCalc && (
                <div className="rounded-md border border-border bg-card p-4 space-y-3" data-testid="calcular-bb-panel">
                    <h4 className="font-display text-lg">Calcular Borrowing Base</h4>
                    <div className="grid md:grid-cols-3 gap-3">
                        <div>
                            <Label>Corte de tape</Label>
                            <Select value={corteId} onValueChange={setCorteId}>
                                <SelectTrigger data-testid="bb-corte-select"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    {cortes.map((c) => (
                                        <SelectItem key={c.corte_id} value={c.corte_id}>
                                            {c.fecha_corte} · {c.registros_validos} filas
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <div className="text-[10px] text-muted-foreground mt-1">
                                Requiere evaluación de elegibilidad previa sobre este corte.
                            </div>
                        </div>
                        <div>
                            <Label>Tipo de cambio (moneda_corte → {facility.moneda})</Label>
                            <Input type="number" step="0.0001" value={fx}
                                onChange={(e) => setFx(e.target.value)}
                                data-testid="bb-fx" />
                        </div>
                    </div>
                    <div className="pt-3 border-t border-border">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Reservas</div>
                            <Button size="sm" variant="ghost" onClick={addReserva} data-testid="add-reserva-btn">
                                <Plus className="h-3 w-3 mr-1" /> Reserva
                            </Button>
                        </div>
                        <table className="trf-table text-xs">
                            <thead><tr>
                                <th>Tipo</th><th>Monto</th><th>Moneda</th><th></th>
                            </tr></thead>
                            <tbody>
                                {reservas.map((r, i) => (
                                    <tr key={i}>
                                        <td>
                                            <select className="border border-border rounded-sm px-1 py-1 bg-background text-xs"
                                                value={r.tipo}
                                                onChange={(e) => setReserva(i, "tipo", e.target.value)}>
                                                {RESERVA_TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </td>
                                        <td>
                                            <Input type="number" step="0.01" value={r.monto} className="w-32"
                                                onChange={(e) => setReserva(i, "monto", e.target.value)}
                                                data-testid={`reserva-monto-${i}`} />
                                        </td>
                                        <td>
                                            <Input value={r.moneda} className="w-20"
                                                onChange={(e) => setReserva(i, "moneda", e.target.value)} />
                                        </td>
                                        <td>
                                            <Button size="sm" variant="ghost" onClick={() => rmReserva(i)}>
                                                <X className="h-3 w-3" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {reservas.length === 0 && (
                                    <tr><td colSpan={4} className="text-center py-4 text-muted-foreground">Sin reservas.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Button onClick={calcular} disabled={busy || !corteId} data-testid="calcular-bb-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Play className="h-4 w-4 mr-1" /> Calcular BB</>)}
                    </Button>
                </div>
            )}

            <div className="rounded-md border border-border bg-card overflow-hidden">
                <div className="px-4 py-2 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground">
                    Histórico BB
                </div>
                <table className="trf-table text-xs" data-testid="bb-historico-table">
                    <thead><tr>
                        <th>Fecha corte</th><th>Calculado</th>
                        <th className="text-right">Bruto elegible</th>
                        <th className="text-right">Reservas</th>
                        <th className="text-right">BB neto</th>
                        <th className="text-right">Cushion</th>
                        <th className="text-right">Utilización %</th>
                    </tr></thead>
                    <tbody>
                        {historico.map((b) => (
                            <tr key={b.id} data-testid={`bb-hist-${b.id}`}>
                                <td className="tabular">{b.fecha_corte}</td>
                                <td className="tabular">{b.calculado_at?.slice(0, 16)}</td>
                                <td className="text-right tabular">{money(b.monto_bruto_elegible, b.moneda_facility)}</td>
                                <td className="text-right tabular">{money(b.reservas_total, b.moneda_facility)}</td>
                                <td className="text-right tabular font-medium">{money(b.borrowing_base_neto, b.moneda_facility)}</td>
                                <td className={`text-right tabular ${Number(b.cushion) < 0 ? "text-[color:var(--brand-coral)]" : "text-[color:var(--brand-exito)]"}`}>
                                    {money(b.cushion, b.moneda_facility)}
                                </td>
                                <td className="text-right tabular">{Number(b.utilizacion_pct || 0).toFixed(1)}%</td>
                            </tr>
                        ))}
                        {historico.length === 0 && (
                            <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Sin cálculos históricos.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
